import { useState } from "react";
import CodeCard from "./CodeCard";
import AddCodePresetModal from "./AddCodePresetModal";
import SummaryEmptyState from "./SummaryEmptyState";

import type {
  AddCodePreset,
  CodeSection,
  CodeStatus,
  EditableCodeValue,
} from "../../utils/codeMetadataUtils";

type CodesPanelProps = {
  codes: EditableCodeValue[];
  emptyMessage: string;
  sourceContext?: {
    contextTitle: string;
    contextValue: string;
  };
  isCollapsed: boolean;
  isAddingCode: boolean;
  onToggleCollapse: () => void;
  onAddCode: (preset: AddCodePreset) => Promise<void>;
  onCodePresetSave: (
    metadataId: number,
    preset: AddCodePreset,
  ) => Promise<void>;
  onConditionTypeChange: (
    metadataId: number,
    conditionType: CodeStatus,
  ) => void;
  onSectionChange: (metadataId: number, section: CodeSection) => Promise<void>;
  onFieldChange: (
    metadataId: number,
    detailId: string,
    field: "date" | "description" | "notes" | "reference" | "pageNumber",
    value: string,
  ) => void;
  onFieldSave: (
    metadataId: number,
    detailId: string,
    field: "date" | "description" | "notes",
    value: string,
  ) => Promise<void>;
  onAssociationChange: (
    metadataId: number,
    documentGroupId: number | null,
    provider: string,
    facility: string,
  ) => void;
  onAssociationSave: (
    metadataId: number,
    documentGroupId: number | null,
    provider: string,
    facility: string,
  ) => Promise<void>;
  getReferenceContext: (args: {
    pageNumber: string;
    documentGroupId?: number | null;
    facility?: string | null;
    provider?: string | null;
  }) => {
    resolvedDocumentGroupId: number | null;
    resolvedFacility: string;
    provider: string;
  };
};

/*
manages collapsible codes panel with header and cards
wraps all code cards and provides collapse functionality
*/
const CodesPanel = ({
  codes,
  emptyMessage,
  sourceContext,
  isCollapsed,
  isAddingCode,
  onToggleCollapse,
  onAddCode,
  onCodePresetSave,
  onConditionTypeChange,
  onSectionChange,
  onFieldChange,
  onFieldSave,
  onAssociationChange,
  onAssociationSave,
  getReferenceContext,
}: CodesPanelProps) => {
  const [isAddCodeModalOpen, setIsAddCodeModalOpen] = useState(false);
  const hasCodes = codes.length > 0;

  return (
    <>
      <div
        className={`metadoc-ai-summary metadoc-ai-summary--visible ${isCollapsed ? "metadoc-ai-summary--collapsed" : ""}`}
      >
        <div className="metadoc-ai-summary__header">
          <div className="metadoc-ai-summary__badge">
            <span className="material-icons">auto_awesome</span>
            <span className="metadoc-ai-summary__badge-text">
              AI Generated Codes
            </span>
          </div>
          <div className="metadoc-ai-summary__header-actions">
            <button
              type="button"
              className="metadoc-ai-summary__context"
              onClick={() => {
                setIsAddCodeModalOpen(true);
              }}
              disabled={isAddingCode}
              title="Add code"
            >
              <span className="metadoc-ai-summary__context-title">Code</span>
              <span className="metadoc-ai-summary__context-value">Add+</span>
            </button>
            {sourceContext && (
              <div className="metadoc-ai-summary__context">
                <span className="metadoc-ai-summary__context-title">
                  {sourceContext.contextTitle}
                </span>
                <span className="metadoc-ai-summary__context-value">
                  {sourceContext.contextValue}
                </span>
              </div>
            )}
            <button
              type="button"
              className="metadoc-ai-summary__collapse-btn"
              onClick={onToggleCollapse}
              title={isCollapsed ? "Expand codes" : "Collapse codes"}
            >
              <span className="material-icons">
                {isCollapsed ? "expand_more" : "expand_less"}
              </span>
            </button>
          </div>
        </div>
        {!isCollapsed && (
          <div
            className={`metadoc-viewer-workspace__codes-panel ${!hasCodes ? "metadoc-viewer-workspace__codes-panel--empty" : ""}`}
          >
            {!hasCodes && <SummaryEmptyState message={emptyMessage} />}
            {hasCodes &&
              codes.map((codeValue) => (
                <CodeCard
                  key={codeValue.id}
                  codeValue={codeValue}
                  onCodePresetSave={onCodePresetSave}
                  onConditionTypeChange={onConditionTypeChange}
                  onSectionChange={onSectionChange}
                  onFieldChange={onFieldChange}
                  onFieldSave={onFieldSave}
                  onAssociationChange={onAssociationChange}
                  onAssociationSave={onAssociationSave}
                  getReferenceContext={getReferenceContext}
                />
              ))}
          </div>
        )}
      </div>
      <AddCodePresetModal
        isOpen={isAddCodeModalOpen}
        isSubmitting={isAddingCode}
        onClose={() => {
          setIsAddCodeModalOpen(false);
        }}
        onSubmit={onAddCode}
      />
    </>
  );
};

export default CodesPanel;
